import { MobileOfferCatalogEntry, MobileOfferPreview, MobileOfferProvider, MobileOfferTerms } from '@numeo/types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { mobileOfferError, mobileOffersApi } from './api'

interface StoreCreationResult {
    state: 'waiting' | 'creating' | 'ready' | 'review'
    message?: string
}
export function useOfferCreation() {
    const [offer, setOffer] = useState<MobileOfferCatalogEntry | null>(null)
    const [previews, setPreviews] = useState<Partial<Record<MobileOfferProvider, MobileOfferPreview>>>({})
    const [results, setResults] = useState<Partial<Record<MobileOfferProvider, StoreCreationResult>>>({})
    const [errors, setErrors] = useState<Partial<Record<MobileOfferProvider, string>>>({})
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const lock = useRef(false)
    const generation = useRef(0)
    const mounted = useRef(true)
    useEffect(() => {
        mounted.current = true
        return () => {
            mounted.current = false
        }
    }, [])
    const clear = useCallback(() => {
        generation.current += 1
        lock.current = false
        setBusy(false)
        setPreviews({})
        setResults({})
        setErrors({})
        setError('')
    }, [])
    const preview = async (terms: MobileOfferTerms, providers: MobileOfferProvider[], saved: (entry: MobileOfferCatalogEntry) => void) => {
        if (lock.current) return
        const request = ++generation.current
        const isCurrent = () => mounted.current && request === generation.current
        lock.current = true
        setBusy(true)
        setError('')
        setErrors({})
        setPreviews({})
        setResults({})
        try {
            const draft = await mobileOffersApi.create(terms)
            if (!isCurrent()) return
            setOffer(draft)
            saved(draft)
            await Promise.all(
                providers.map(async (provider) => {
                    try {
                        const quote = await mobileOffersApi.preview(draft.id, provider)
                        if (isCurrent()) setPreviews((previous) => ({ ...previous, [provider]: quote }))
                    } catch (failure) {
                        if (isCurrent()) setErrors((previous) => ({ ...previous, [provider]: mobileOfferError(failure) }))
                    }
                })
            )
        } catch (failure) {
            if (isCurrent()) setError(mobileOfferError(failure))
        } finally {
            if (isCurrent()) {
                lock.current = false
                setBusy(false)
            }
        }
    }
    const create = async (providers: MobileOfferProvider[]) => {
        if (lock.current || !offer || providers.some((provider) => previews[provider]?.offerId !== offer.id || previews[provider]?.provider !== provider))
            return
        const request = ++generation.current
        const isCurrent = () => mounted.current && request === generation.current
        lock.current = true
        setBusy(true)
        setError('')
        const id = offer.id
        const recoveries = new Map<MobileOfferProvider, StoreCreationResult>()
        const unknownOutcome = 'Outcome unknown. Open offer details and refresh creation status before retrying.'
        await Promise.all(
            providers.map(async (provider) => {
                const quote = previews[provider]
                if (!quote) return
                if (offer.stores[provider].status === 'ready' || results[provider]?.state === 'ready') {
                    setResults((previous) => ({ ...previous, [provider]: { state: 'ready' } }))
                    return
                }
                setResults((previous) => ({ ...previous, [provider]: { state: 'creating' } }))
                try {
                    const operation = await mobileOffersApi.provision(quote)
                    if (isCurrent())
                        setResults((previous) => ({
                            ...previous,
                            [provider]: {
                                state: operation.status === 'ready' ? 'ready' : 'review',
                                message: operation.status === 'ready' ? undefined : `Store reports ${operation.status}. Check status before retrying.`,
                            },
                        }))
                } catch (failure) {
                    // A timeout may follow a committed write. Reconcile instead of silently creating again.
                    if (!isCurrent()) return
                    try {
                        const operation = await mobileOffersApi.status(id, provider)
                        const ready = operation?.status === 'ready'
                        recoveries.set(provider, { state: ready ? 'ready' : 'review', message: ready ? undefined : mobileOfferError(failure) })
                    } catch {
                        recoveries.set(provider, { state: 'review', message: unknownOutcome })
                    }
                }
            })
        )
        if (!isCurrent()) return
        // Read once after every store attempt and recovery lookup has settled.
        let current: MobileOfferCatalogEntry | null = null
        try {
            current = await mobileOffersApi.find(id)
        } catch {
            /* Confirmed per-store success survives an unavailable catalog read. */
        }
        if (isCurrent()) {
            if (current) setOffer(current)
            for (const [provider, recovery] of recoveries) {
                const ready = recovery.state === 'ready' || current?.stores[provider].status === 'ready'
                const message = ready ? undefined : current ? recovery.message : unknownOutcome
                setResults((previous) => ({ ...previous, [provider]: { state: ready ? 'ready' : 'review', message } }))
            }
            lock.current = false
            setBusy(false)
        }
    }
    return {
        offer,
        setOffer,
        previews,
        results,
        errors,
        busy,
        error,
        preview,
        create,
        clear,
    }
}
